import React from "react";
import { Typography } from "@mui/material";
import { PieChart, Work } from "@mui/icons-material";
import { ReactComponent as ProjectsIcon } from "../../../assets/svg/icon-menu-projects.svg";
import { ReactComponent as ToolsIcon } from "../../../assets/svg/icon-tools.svg";
import { ReactComponent as UserIcon } from "../../../assets/svg/icon-menu-user.svg";
import { ReactComponent as VideoIcon } from "../../../assets/svg/icon-video.svg";
import { ReactComponent as ProductIcon } from "../../../assets/svg/icon-product.svg";
import { ReactComponent as ContentTypeIcon } from "../../../assets/svg/content-type.svg";
import { ReactComponent as VideoProductionIcon } from "../../../assets/svg/video-production.svg";
import { ReactComponent as DashboardIcon } from "../../../assets/svg/icon-dashboard.svg";

// eslint-disable-next-line import/no-cycle
import ProListing from "../../../pages/pro/Listing";
import { ExpertsV2Listing } from "../../../pages/brand/expertsV2/Experts";
import { CreateProject } from "../../../pages/brand/projects/createProject";
import { SelectVideos } from "../../../pages/brand/projects/selectVideos";
import { SelectDistribution } from "../../../pages/brand/projects/selectDistribution";
import { AdminVideosV2 } from "../../../pages/admin/videos/adminVideosV2";
import { EmbedV2Listing } from "../../../pages/admin/embeds/embedsV2";
import VideoShortlists from "../../../pages/admin/VideoShortlists";
import VideoShortlistDetail from "../../../pages/admin/VideoShortlists/VideoShortlistDetail";
import {
  IconBook,
  IconPencil,
  IconQr,
  IconSales,
  IconSearch,
  IconUser,
} from "../../../utils/icons";

export interface NavLinkItem {
  link: string;
  label: JSX.Element | string;
  external?: boolean;
}
export interface NavBarItem extends NavLinkItem {
  dropdownOptions?: Array<NavLinkItem>;
}

export type NavLinksInterface = Record<string, NavLinkItem>[];

const VideoNavItem: NavBarItem = {
  link: "/videos",
  label: (
    <>
      <VideoIcon />
      <Typography marginLeft={1}>Videos</Typography>
    </>
  ),
  dropdownOptions: [
    {
      link: "/videos",
      label: (
        <>
          <IconSearch stroke="currentColor" />
          <Typography marginLeft={1}>Search</Typography>
        </>
      ),
    },
    {
      link: "/videos/shortlists",
      label: (
        <>
          <IconBook size={18} stroke="currentColor" />
          <Typography marginLeft={1}>Video Shortlists</Typography>
        </>
      ),
    },
  ],
};

const MyVideoNavItem: NavLinkItem = {
  link: "/my-videos",
  label: (
    <>
      <VideoIcon />
      <Typography marginLeft={1}>Videos</Typography>
    </>
  ),
};

const MyBrandsNavItem: NavLinkItem = {
  link: "/my-brands",
  label: (
    <>
      <ProjectsIcon />
      <Typography marginLeft={1}>Brands</Typography>
    </>
  ),
};

// Content types / channels / placements / categories moved to tabs on the
// pricing page, so Brands is a plain link now.
const BrandsNavItem: NavBarItem = {
  link: "/brands",
  label: (
    <>
      <ProjectsIcon />
      <Typography marginLeft={1}>Brands</Typography>
    </>
  ),
};

const CategoriesNavItem: NavLinkItem = {
  link: "/categories",
  label: (
    <>
      <IconBook size={18} stroke="currentColor" />
      <Typography marginLeft={1}>Legacy B2B Categories</Typography>
    </>
  ),
};

const ProductCategoriesNavItem: NavLinkItem = {
  link: "/product-categories",
  label: (
    <>
      <IconBook size={18} stroke="currentColor" />
      <Typography marginLeft={1}>Categories</Typography>
    </>
  ),
};

const AttributesNavItem: NavLinkItem = {
  link: "/attributes",
  label: (
    <>
      <ContentTypeIcon stroke="currentColor" />
      <Typography marginLeft={1}>Attributes</Typography>
    </>
  ),
};

const ProductNavItem: NavBarItem = {
  link: "/products",
  label: (
    <>
      <ProductIcon />
      <Typography marginLeft={1}>Products</Typography>
    </>
  ),
  dropdownOptions: [
    ProductCategoriesNavItem,
    CategoriesNavItem,
    AttributesNavItem,
  ],
};

export const PDP_AUDIT_URL = "https://thedesirecompany.com/pdp-audit";

const FilenameGenNavItem: NavBarItem = {
  link: "",
  label: (
    <>
      <ToolsIcon />
      <Typography marginLeft={1}>Tools</Typography>
    </>
  ),
  dropdownOptions: [
    {
      link: "/qr-generator",
      label: (
        <>
          <IconQr size={16} stroke="currentColor" />
          <Typography marginLeft={1.2}>QR Code Generator</Typography>
        </>
      ),
    },
  ],
};

export const InternalExpertsNav: NavBarItem = {
  link: "/experts",
  label: (
    <>
      <UserIcon />
      <Typography marginLeft={1}>Experts</Typography>
    </>
  ),
  dropdownOptions: [
    {
      link: "/experts",
      label: (
        <>
          <IconSearch stroke="currentColor" />
          <Typography marginLeft={1}>Search</Typography>
        </>
      ),
    },
    {
      link: "/experts/shortlists",
      label: (
        <>
          <IconBook size={18} stroke="currentColor" />
          <Typography marginLeft={1}>Shortlists</Typography>
        </>
      ),
    },
    {
      link: "/professions",
      label: (
        <>
          <IconUser width={18} height={18} stroke="currentColor" />
          <Typography marginLeft={1}>Professions</Typography>
        </>
      ),
    },
    {
      link: "/areas-of-focus",
      label: (
        <>
          <IconUser width={18} height={18} stroke="currentColor" />
          <Typography marginLeft={1}>Areas of Focus</Typography>
        </>
      ),
    },
    {
      link: "/experts/expert-iq",
      label: (
        <>
          <DashboardIcon size={18} stroke="currentColor" />
          <Typography marginLeft={1}>Expert IQ</Typography>
        </>
      ),
    },
    AttributesNavItem,
  ],
};

export const ExpertTeamDashNav = {
  link: "/dashboard",
  label: (
    <>
      <PieChart />
      <Typography marginLeft={1}>Dashboard</Typography>
    </>
  ),
};

export const ProfessionsPageNav = {
  link: "/professions",
  label: (
    <>
      <Work />
      <Typography marginLeft={1}>Professions</Typography>
    </>
  ),
};

export const BrowseProPaths = [
  {
    path: "/experts",
    element: <ProListing />,
  },
];

export const BrandProPaths = [
  {
    path: "/experts",
    element: <ExpertsV2Listing />,
  },
];

export const BrandTeamCreateProjectDistribution = [
  {
    path: "/projects/new",
    element: <CreateProject />,
  },
  {
    path: "/brands/:brandId/projects/new",
    element: <CreateProject />,
  },
  {
    path: "/brands/:brandId/projects/:projectId/group/:groupId",
    element: <SelectVideos />,
  },
  {
    path: "/brands/:brandId/projects/:projectId/group/:groupId/distribution",
    element: <SelectDistribution />,
  },
];

export const VideoPaths = [
  {
    path: "/videos",
    element: <AdminVideosV2 />,
  },
  {
    // Alias: shared components (product detail, project media tab) deep-link to
    // the brand-portal path /videos/tdc?video=X. Brand routers mount their own
    // page here; for internal roles it's the same videos listing.
    path: "/videos/tdc",
    element: <AdminVideosV2 />,
  },
  {
    path: "/videos/embeds",
    element: <EmbedV2Listing />,
  },
  {
    path: "/videos/shortlists",
    element: <VideoShortlists />,
  },
  {
    path: "/videos/shortlists/:id",
    element: <VideoShortlistDetail />,
  },
];

export const ExpertProfilePath = [
  {
    path: "/experts/:expertId/:slug",
    element: <ExpertsV2Listing />,
  },
];

export const VideoProductionNav: NavBarItem = {
  link: "/video-production/orders",
  label: (
    <>
      <VideoProductionIcon />
      <Typography className="ml-2">
        Video Production
      </Typography>
    </>
  ),
  dropdownOptions: [
    {
      link: "/video-production/sow",
      label: (
        <>
          <IconBook size={18} stroke="currentColor" />
          <Typography marginLeft={1}>SOW</Typography>
        </>
      ),
    },
    {
      link: "/video-production/orders",
      label: (
        <>
          <IconPencil size={18} stroke="currentColor" />
          <Typography marginLeft={1}>Orders</Typography>
        </>
      ),
    },
    {
      link: "/video-production/projects",
      label: (
        <>
          <ProjectsIcon />
          <Typography marginLeft={1}>Projects</Typography>
        </>
      ),
    },
  ],
};

const DashboardNavItem: NavLinkItem = {
  link: "/dashboard",
  label: (
    <>
      <DashboardIcon />
      <Typography marginLeft={1}>Dashboard</Typography>
    </>
  ),
};

export const SalesNav: NavBarItem = {
  link: "/sales/leads",
  label: (
    <>
      <IconSales />
      <Typography marginLeft={1}>Sales</Typography>
    </>
  ),
  dropdownOptions: [
    {
      link: "/sales/leads",
      label: (
        <>
          <IconUser width={18} height={18} stroke="currentColor" />
          <Typography marginLeft={1}>Leads</Typography>
        </>
      ),
    },
    {
      link: "/sales/pdp-audit-reports",
      label: (
        <>
          <IconSearch width={18} height={18} stroke="currentColor" />
          <Typography marginLeft={1}>PDP Audit</Typography>
        </>
      ),
    },
    {
      link: "/sales/consumer-voice-reports",
      label: (
        <>
          <IconSearch width={18} height={18} stroke="currentColor" />
          <Typography marginLeft={1}>Consumer Voice</Typography>
        </>
      ),
    },
    {
      link: "/sales/ai-presence-reports",
      label: (
        <>
          <IconSearch width={18} height={18} stroke="currentColor" />
          <Typography marginLeft={1}>AI Presence</Typography>
        </>
      ),
    },
    {
      link: "/sales/prep-packs",
      label: (
        <>
          <IconBook size={18} stroke="currentColor" />
          <Typography marginLeft={1}>Sales Intel Prep Pack</Typography>
        </>
      ),
    },
    {
      link: "/sales/proposals",
      label: (
        <>
          <IconPencil size={18} stroke="currentColor" />
          <Typography marginLeft={1}>Strategies</Typography>
        </>
      ),
    },
  ],
};

// Pricing sits in the Sales menu. All internal roles can view the pricing
// page; editing is gated in-page (and server-side) to admin / account
// manager admin / sales.
const SalesNavWithPricing: NavBarItem = {
  ...SalesNav,
  dropdownOptions: [
    ...(SalesNav.dropdownOptions || []),
    {
      link: "/admin/pricing",
      label: (
        <>
          <ToolsIcon width={18} height={18} />
          <Typography marginLeft={1}>Pricing</Typography>
        </>
      ),
    },
    {
      link: "/pricing",
      label: (
        <>
          <ToolsIcon width={18} height={18} />
          <Typography marginLeft={1}>Plans &amp; Pricing</Typography>
        </>
      ),
    },
    {
      link: "/admin/brands/usage",
      label: (
        <>
          <PieChart fontSize="small" />
          <Typography marginLeft={1}>Brand Usage</Typography>
        </>
      ),
    },
  ],
};

// Every internal role sees Autopilot (the route and the API agree).
const AutopilotModelsNavItem: NavBarItem = {
  link: "/admin/autopilot/model-runs",
  label: (
    <>
      <PieChart fontSize="small" />
      <Typography marginLeft={1}>Autopilot Models</Typography>
    </>
  ),
};

const InternalRoleLinks = [
  DashboardNavItem,
  VideoProductionNav,
  BrandsNavItem,
  VideoNavItem,
  ProductNavItem,
  SalesNavWithPricing,
  InternalExpertsNav,
  FilenameGenNavItem,
  AutopilotModelsNavItem,
];

export const NavLinks = {
  ADMIN: [
    DashboardNavItem,
    VideoProductionNav,
    BrandsNavItem,
    VideoNavItem,
    ProductNavItem,
    {
      link: "/users",
      label: (
        <>
          <UserIcon />
          <Typography marginLeft={1}>Users</Typography>
        </>
      ),
    },
    SalesNavWithPricing,
    InternalExpertsNav,
    FilenameGenNavItem,
    AutopilotModelsNavItem,
  ],
  INTERNAL: InternalRoleLinks,
  PRO_TEAM_ADMIN: InternalRoleLinks,
  SALES_TEAM: InternalRoleLinks,
  ACCOUNT_MANAGER_ADMIN: InternalRoleLinks,
  POST_PRODUCTION_ADMIN: InternalRoleLinks,
  PRO: [
    {
      link: "/expert/projects",
      label: (
        <>
          <ProjectsIcon />
          <Typography marginLeft={1}>Projects</Typography>
        </>
      ),
    },
    MyVideoNavItem,
    MyBrandsNavItem,
    {
      link: "/settings/profile",
      label: (
        <>
          <UserIcon />
          <Typography marginLeft={1}>Profile</Typography>
        </>
      ),
    },
  ],
  // Brand-portal roles (BRAND_ADMIN / BRAND_MEMBER / BRAND_TEAM_SALES) don't
  // read NavLinks anymore: their sectioned nav lives in
  // components/SideNav/brandNav.tsx (TDC-1499).
};
